import { useEffect, useState } from "react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, CheckCircle, Prohibit, Trash, Lightning, BellRinging, Pause, Play } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";

const badge = {
  unpaid: "bg-amber-50 text-amber-700 border-amber-200",
  partial: "bg-blue-50 text-blue-700 border-blue-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  void: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Billing() {
  const [params] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [invOpen, setInvOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [inv, setInv] = useState({ student_id: "", payer_email: "", description: "Weekly Tuition", amount: "", due_date: "", service_dates: "", notes: "" });
  const [plan, setPlan] = useState({ student_id: "", payer_email: "", description: "Weekly Tuition", amount: "", frequency: "weekly", start_date: "" });

  const load = async () => {
    const [i, p, s, f, t] = await Promise.all([api.get("/invoices"), api.get("/billing-plans"), api.get("/students"), api.get("/fees"), api.get("/payment-transactions")]);
    setInvoices(i.data); setPlans(p.data); setStudents(s.data); setFees(f.data); setTransactions(t.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (["charge", "add"].includes(params.get("action"))) setInvOpen(true); }, [params]);

  const pickStudent = (setter, obj) => (v) => {
    const s = students.find((x) => x.id === v);
    setter({ ...obj, student_id: v, payer_email: s?.parent_email || obj.payer_email });
  };

  const createInvoice = async () => {
    try {
      await api.post("/invoices", { ...inv, amount: parseFloat(inv.amount) });
      toast.success("Invoice created");
      setInvOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createPlan = async () => {
    try {
      await api.post("/billing-plans", { ...plan, amount: parseFloat(plan.amount), active: true });
      toast.success("Billing plan created");
      setPlanOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const markPaid = async (id) => {
    try { await api.patch(`/invoices/${id}/mark-paid`); toast.success("Marked paid (offline)"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const voidInv = async (id) => {
    try { await api.delete(`/invoices/${id}`); toast.success("Invoice voided"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delPlan = async (id) => {
    try { await api.delete(`/billing-plans/${id}`); toast.success("Plan removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const togglePlan = async (id) => {
    try {
      const { data } = await api.post(`/billing-plans/${id}/toggle`);
      toast.success(data.active ? "Plan resumed" : "Plan paused");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const runNow = async () => {
    try {
      const { data } = await api.post("/billing-plans/run-now");
      toast.success(data.invoices_created
        ? `Created ${data.invoices_created} invoice(s) and notified families`
        : "No plans are due yet — nothing to invoice");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remind = async (id) => {
    try { await api.post(`/invoices/${id}/remind`); toast.success("Reminder sent to the family"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const outstanding = invoices.filter((i) => i.status === "unpaid").reduce((a, i) => a + Number(i.amount), 0);
  const collected = invoices.filter((i) => i.status === "paid").reduce((a, i) => a + Number(i.amount_paid || 0), 0);
  const shownInvoices = invoices.filter((i) => {
    if (params.get("id") && i.id !== params.get("id")) return false;
    const filter = params.get("filter"); const aging = params.get("aging");
    if (filter === "paid") return i.status === "paid";
    if (filter === "unpaid") return ["unpaid", "partial"].includes(i.status);
    const pastDue = ["unpaid", "partial"].includes(i.status) && i.due_date && i.due_date < new Date().toISOString().slice(0, 10);
    if (filter === "past_due" && !pastDue) return false;
    if (aging) {
      if (!pastDue) return false;
      const days = Math.floor((Date.now() - new Date(`${i.due_date}T12:00:00`).getTime()) / 864e5);
      return aging === "1_30" ? days <= 30 : aging === "31_60" ? days > 30 && days <= 60 : aging === "61_90" ? days > 60 && days <= 90 : days > 90;
    }
    return !filter || ["paid", "unpaid", "past_due"].includes(filter);
  });

  const StudentSelect = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid={testid}><SelectValue placeholder="Choose a student" /></SelectTrigger>
      <SelectContent>
        {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="billing-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Billing</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Tuition & invoices</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" onClick={runNow} className="rounded-full h-12 px-6 font-bold"
                  data-testid="run-billing-btn"><Lightning size={18} className="mr-2" /> Run billing now</Button>
          <Button variant="outline" onClick={() => setPlanOpen(true)} className="rounded-full h-12 px-6 font-bold"
                  data-testid="new-plan-btn">New billing plan</Button>
          <Button onClick={() => setInvOpen(true)} className="rounded-full h-12 px-6 bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="new-invoice-btn"><Plus size={18} className="mr-2" /> New invoice</Button>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        {[["Outstanding", money(outstanding), "text-[#EF4444]", "stat-outstanding"],
          ["Collected", money(collected), "text-[#10B981]", "stat-collected"],
          ["Active plans", plans.length, "text-[#3B82F6]", "stat-plans"]].map(([k, v, c, tid]) => (
          <div key={k} className="bg-white rounded-2xl border border-border p-6" data-testid={tid}>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-muted-foreground">{k}</p>
            <p className={`mt-3 font-display text-3xl font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="invoices" className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="invoices" className="rounded-full font-bold" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="plans" className="rounded-full font-bold" data-testid="tab-plans">Recurring plans</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-6 space-y-3" data-testid="invoices-list">
          {params.get("filter") === "late_fees" && fees.filter((f) => ["pending", "posted"].includes(f.status)).map((fee) => <div key={fee.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"><div className="flex-1"><p className="font-bold">{fee.student_name} · {fee.type.replaceAll("_", " ")}</p><p className="text-xs text-muted-foreground mt-1">{fee.date} · {fee.minutes || 0} minutes · {fee.payer_email}</p></div><p className="font-display text-xl font-bold">{money(fee.amount)}</p><span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${badge[fee.status] || badge.unpaid}`}>{fee.status}</span></div>)}
          {params.get("filter") === "failed" && transactions.filter((t) => t.status === "failed").map((tx) => <div key={tx.id} className="bg-white rounded-2xl border border-red-200 p-5 flex flex-wrap gap-4 items-center"><div className="flex-1"><p className="font-bold">{tx.invoice_number || "Payment attempt"} · {tx.student_name || "Unknown student"}</p><p className="text-xs text-muted-foreground mt-1">{tx.payer_email} · {tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"}</p>{tx.failure_message && <p className="text-sm text-red-600 mt-2">{tx.failure_message}</p>}</div><p className="font-display text-xl font-bold">{money(tx.amount)}</p><span className="text-xs font-bold px-3 py-1.5 rounded-full border bg-red-50 text-red-700">Failed</span></div>)}
          {shownInvoices.length === 0 && !(params.get("filter") === "late_fees" && fees.some((f) => ["pending", "posted"].includes(f.status))) && !(params.get("filter") === "failed" && transactions.some((t) => t.status === "failed")) && <p className="text-muted-foreground">No billing records match this dashboard filter.</p>}
          {shownInvoices.map((i) => (
            <div key={i.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                 data-testid={`invoice-row-${i.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold">{i.number} · {i.student_name}</p>
                <p className="text-xs text-muted-foreground mt-1 font-semibold">
                  {i.description} · {i.payer_email} · due {i.due_date}
                </p>
              </div>
              <p className="font-display text-xl font-bold">{money(i.amount)}</p>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${badge[i.status]}`}
                    data-testid={`invoice-status-${i.id}`}>{i.status}</span>
              {i.status === "unpaid" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => markPaid(i.id)}
                          className="rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold" data-testid={`mark-paid-${i.id}`}>
                    <CheckCircle size={16} className="mr-1" /> Mark paid
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remind(i.id)}
                          className="rounded-full font-bold" data-testid={`remind-${i.id}`}>
                    <BellRinging size={15} className="mr-1" /> Remind
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => voidInv(i.id)}
                          className="rounded-full font-bold text-[#EF4444]" data-testid={`void-invoice-${i.id}`}>
                    <Prohibit size={15} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="plans" className="mt-6 space-y-3" data-testid="plans-list">
          {plans.length === 0 && <p className="text-muted-foreground">No recurring billing plans yet.</p>}
          {plans.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                 data-testid={`plan-row-${p.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold">{p.student_name}
                  {p.active === false && <span className="ml-2 text-xs font-bold text-muted-foreground">paused</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-semibold">
                  {p.description} · {p.frequency} · next invoice {p.next_run || p.start_date} · {p.payer_email}
                </p>
              </div>
              <p className="font-display text-xl font-bold">{money(p.amount)}</p>
              <Button size="sm" variant="outline" onClick={() => togglePlan(p.id)}
                      className="rounded-full font-bold" data-testid={`toggle-plan-${p.id}`}>
                {p.active === false ? <Play size={15} /> : <Pause size={15} />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => delPlan(p.id)}
                      className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-plan-${p.id}`}>
                <Trash size={15} />
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="rounded-3xl" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-display">New invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm font-bold">Student</Label>
              <StudentSelect value={inv.student_id} onChange={pickStudent(setInv, inv)} testid="invoice-student" /></div>
            <div><Label className="text-sm font-bold">Payer email</Label>
              <Input type="email" value={inv.payer_email} onChange={(e) => setInv({ ...inv, payer_email: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="invoice-payer" /></div>
            <div><Label className="text-sm font-bold">Description</Label>
              <Input value={inv.description} onChange={(e) => setInv({ ...inv, description: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="invoice-description" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold">Amount (USD)</Label>
                <Input type="number" step="0.01" value={inv.amount} onChange={(e) => setInv({ ...inv, amount: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="invoice-amount" /></div>
              <div><Label className="text-sm font-bold">Due date</Label>
                <Input type="date" value={inv.due_date} onChange={(e) => setInv({ ...inv, due_date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="invoice-due" /></div>
            </div>
            <Button onClick={createInvoice} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="invoice-save-btn">Create invoice</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="rounded-3xl" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-display">New recurring billing plan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm font-bold">Student</Label>
              <StudentSelect value={plan.student_id} onChange={pickStudent(setPlan, plan)} testid="plan-student" /></div>
            <div><Label className="text-sm font-bold">Payer email</Label>
              <Input type="email" value={plan.payer_email} onChange={(e) => setPlan({ ...plan, payer_email: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="plan-payer" /></div>
            <div><Label className="text-sm font-bold">Description</Label>
              <Input value={plan.description} onChange={(e) => setPlan({ ...plan, description: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="plan-description" /></div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label className="text-sm font-bold">Amount</Label>
                <Input type="number" step="0.01" value={plan.amount} onChange={(e) => setPlan({ ...plan, amount: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="plan-amount" /></div>
              <div><Label className="text-sm font-bold">Frequency</Label>
                <Select value={plan.frequency} onValueChange={(v) => setPlan({ ...plan, frequency: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="plan-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["weekly", "biweekly", "twice-monthly", "monthly", "quarterly", "annual"].map((x) =>
                      <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-sm font-bold">Start date</Label>
                <Input type="date" value={plan.start_date} onChange={(e) => setPlan({ ...plan, start_date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="plan-start" /></div>
            </div>
            <Button onClick={createPlan} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="plan-save-btn">Create plan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
